import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { adminSupabase as supabase } from '@/integrations/supabase/adminClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, Loader2, CheckCircle, Copy } from 'lucide-react';
import { BrandedSpinner } from '@/components/ui/branded-spinner';
import { toast } from 'sonner';
import paynovusLogo from '@/assets/paynovus-logo-horizontal-tagline.png';

function describeError(err: unknown, fallback: string): string {
  if (!err) return fallback;
  if (typeof err === 'string') return err.trim() || fallback;
  const anyErr = err as any;
  const candidates = [anyErr?.message, anyErr?.error_description, anyErr?.error, anyErr?.msg, anyErr?.hint];
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim() && c.trim() !== '{}') return c.trim();
  }
  if (typeof anyErr?.status === 'number') {
    if (anyErr.status === 504 || anyErr.status === 408) {
      return 'The authentication service timed out. Please wait a few seconds and try again.';
    }
    return `${fallback} (HTTP ${anyErr.status})`;
  }
  const serialized = (() => {
    try {
      return JSON.stringify(anyErr);
    } catch {
      return '';
    }
  })();
  if (serialized && serialized !== '{}' && serialized.length < 200) return `${fallback} (${serialized})`;
  return fallback;
}

/** Never let a hanging auth call freeze the enrollment screen. */
async function withTimeout<T>(p: Promise<T>, ms = 15000): Promise<T> {
  return await Promise.race([
    p,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error('The authentication service timed out. Please try again.')), ms),
    ),
  ]);
}

export default function AdminMFASetup() {
  const { user, adminUser, adminUserLoaded, loading } = useAdminAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<'loading' | 'enroll' | 'verify'>('loading');
  const [factorId, setFactorId] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [contextTimedOut, setContextTimedOut] = useState(false);
  const bootstrappedRef = useRef(false);
  const activeFactorRef = useRef<string>('');

  // The admin_users / getUser calls can hang (auth gateway 504s). Don't let that
  // leave the enrollment screen stuck on a spinner forever.
  useEffect(() => {
    const t = setTimeout(() => setContextTimedOut(true), 6000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const contextReady = (!loading && adminUserLoaded) || contextTimedOut;
    if (!contextReady) return;
    // Guard: run the enrollment bootstrap exactly once. Re-running it would
    // unenroll the pending factor whose QR code is already on screen.
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;
    void bootstrap();
  }, [loading, adminUserLoaded, user, contextTimedOut]);

  async function bootstrap() {
    // Resolve the session directly — the context may still be resolving.
    let hasSession = !!user;
    if (!hasSession) {
      try {
        const { data } = await withTimeout(supabase.auth.getSession(), 8000);
        hasSession = !!data?.session;
      } catch {
        hasSession = false;
      }
    }
    if (!hasSession) {
      navigate('/admin/login');
      return;
    }
    await checkMFAStatus();
  }

  async function checkMFAStatus() {
    setError(null);
    setStep('loading');
    let data: any;
    try {
      const res = await withTimeout(supabase.auth.mfa.listFactors());
      if (res.error) throw res.error;
      data = res.data;
    } catch (err) {
      setError(describeError(err, 'Could not load your MFA factors.'));
      setStep('enroll');
      return;
    }
    const verifiedFactor = data?.totp?.find((f: any) => f.factor_type === 'totp' && f.status === 'verified');
    if (verifiedFactor) {
      navigate('/admin');
      return;
    }

    // Clean up stale unverified factors before creating a fresh one
    const unverified = data?.totp?.filter((f: any) => f.status !== 'verified') || [];
    for (const f of unverified) {
      try {
        await withTimeout(supabase.auth.mfa.unenroll({ factorId: f.id }), 8000);
      } catch {
        /* stale factor cleanup is best-effort */
      }
    }

    // Start enrollment
    await enrollMFA();
  }

  async function enrollMFA() {
    setError(null);
    try {
      const { data, error: enrollErr } = await withTimeout(
        supabase.auth.mfa.enroll({
          factorType: 'totp',
          friendlyName: `PayNovus Admin TOTP ${Date.now()}`,
        }),
      );
      if (enrollErr || !data) throw enrollErr ?? new Error('No enrollment data returned.');

      activeFactorRef.current = data.id;
      setFactorId(data.id);
      setQrCode(data.totp.qr_code);
      setSecret(data.totp.secret);
    } catch (err) {
      setError(describeError(err, 'Could not start MFA enrollment. Please try again.'));
    } finally {
      setStep('enroll');
    }
  }

  async function handleVerify() {
    setError(null);
    setIsVerifying(true);

    try {
      const currentFactorId = activeFactorRef.current || factorId;
      const { data: challenge, error: challengeErr } = await supabase.auth.mfa.challenge({
        factorId: currentFactorId,
      });

      if (challengeErr || !challenge) {
        const msg = describeError(challengeErr, 'Could not start MFA verification.');
        if (/not found|invalid.*factor|does not exist/i.test(msg)) {
          setError('Your enrollment session expired. A new QR code has been generated — scan it and try again.');
          setVerifyCode('');
          await enrollMFA();
          return;
        }
        setError(msg);
        return;
      }

      const { error: verifyErr } = await supabase.auth.mfa.verify({
        factorId: currentFactorId,
        challengeId: challenge.id,
        code: verifyCode,
      });

      if (verifyErr) {
        const msg = describeError(verifyErr, 'Invalid code. Please try again.');
        setError(/invalid|incorrect/i.test(msg) ? 'Invalid code. Please try again.' : msg);
        return;
      }

      // Mark MFA as enabled in admin_users
      await supabase
        .from('admin_users')
        .update({
          mfa_enabled: true,
          mfa_enrolled_at: new Date().toISOString(),
          mfa_method: 'totp',
        })
        .eq('user_id', user!.id);

      toast.success('MFA enrolled successfully');
      navigate('/admin');
    } catch (err) {
      setError(describeError(err, 'Verification failed. Please try again.'));
    } finally {
      setIsVerifying(false);
    }
  }


  if (step === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <BrandedSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <img src={paynovusLogo} alt="PayNovus" className="h-12" />
          </div>
          <div className="flex items-center justify-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            <CardTitle className="text-2xl">MFA Enrollment Required</CardTitle>
          </div>
          <CardDescription>
            Multi-factor authentication is mandatory for admin access. 
            Set up your authenticator app to continue.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {step === 'enroll' && !qrCode && (
            <Button className="w-full" variant="outline" onClick={() => void checkMFAStatus()}>
              Retry MFA setup
            </Button>
          )}

          {step === 'enroll' && qrCode && (
            <>
              <div className="space-y-4">
                <div className="text-sm text-muted-foreground">
                  <strong>Step 1:</strong> Scan this QR code with your authenticator app 
                  (Google Authenticator, Authy, 1Password, etc.)
                </div>
                
                <div className="flex justify-center p-4 bg-white rounded-lg">
                  <img src={qrCode} alt="MFA QR Code" className="w-48 h-48" />
                </div>

                <div className="text-sm text-muted-foreground">
                  <strong>Or enter this secret manually:</strong>
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2 bg-muted rounded text-xs break-all font-mono">
                    {secret}
                  </code>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(secret);
                      toast.info('Secret copied');
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-3 pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  <strong>Step 2:</strong> Enter the 6-digit code from your authenticator app
                </div>
                <div className="space-y-2">
                  <Label htmlFor="code">Verification Code</Label>
                  <Input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder=""
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                    className="text-center text-2xl tracking-widest"
                    disabled={isVerifying}
                  />
                </div>
                <Button
                  className="w-full"
                  onClick={handleVerify}
                  disabled={verifyCode.length !== 6 || isVerifying}
                >
                  {isVerifying ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Verify & Enable MFA
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
